import {planCaptionTranslation,type CaptionTranslationPlan} from './caption-translation-plan';
import {captionTranslationTargets} from './caption-translation-targets';
import {setCaptionDisplayLanguage} from '../../vendor/src/features/remotion-editor/editor/state/actions/caption-translation-actions';
import type {EditorState} from '../../vendor/src/features/remotion-editor/editor/state/types';
import type {CaptionTranslationRequest} from '../../protocol/caption-translation-request';

export function translationControlPlan(state:EditorState,language:'en'|'ko',selectedOnly:boolean):CaptionTranslationPlan{
 const plan=planCaptionTranslation(state,language);
 if(!selectedOnly)return plan;
 const ids=new Set<string>();
 const visit=(id:string,depth:number)=>{if(ids.has(id)||depth>64)return;ids.add(id);const item=state.undoableState.items[id];if(item?.type==='group')item.children.forEach(child=>visit(child,depth+1));};
 state.selectedItems.forEach(id=>visit(id,0));
 const assets=new Set([...ids].flatMap(id=>{const item=state.undoableState.items[id];return item?.type==='captions'?[item.assetId]:[];}));
 const slots=plan.slots.filter(slot=>assets.has(slot.key)),selectedAssets=new Set(slots.map(slot=>slot.key));
 if(Object.values(state.undoableState.items).some(item=>item.type==='captions'&&selectedAssets.has(item.assetId)&&!ids.has(item.id)))throw Error('같은 자막을 쓰는 다른 클립도 함께 선택하거나 전체 범위로 번역해 주세요.');
 return {...plan,slots,titles:plan.titles.filter(title=>ids.has(title.key))};
}
export function planForTranslationRequest(state:EditorState,request:CaptionTranslationRequest){
 const plan=planCaptionTranslation(state,request.targetLanguage);
 return {...plan,slots:plan.slots.filter(slot=>request.assetIds.includes(slot.key)),titles:plan.titles.filter(title=>request.titleIds.includes(title.key))};
}
export async function translationPlanHash(plan:CaptionTranslationPlan){
 const bytes=new TextEncoder().encode(JSON.stringify({targetLanguage:plan.targetLanguage,slots:plan.slots,titles:plan.titles}));
 const digest=await crypto.subtle.digest('SHA-256',bytes);
 return [...new Uint8Array(digest)].map(byte=>byte.toString(16).padStart(2,'0')).join('');
}
export function translationPreview(raw:unknown):string[]{
 const record=(value:unknown):value is Record<string,unknown>=>!!value&&typeof value==='object'&&!Array.isArray(value);
 if(!record(raw))return [];
 const rows:string[]=[];
 if(Array.isArray(raw.titles))for(const title of raw.titles){if(record(title)&&typeof title.text==='string')rows.push(title.text.slice(0,10000));if(rows.length>=200)return rows;}
 if(Array.isArray(raw.slots))for(const slot of raw.slots){
  if(!record(slot)||!record(slot.translation)||!Array.isArray(slot.translation.cues))continue;
  for(const cue of slot.translation.cues){if(record(cue)&&typeof cue.text==='string')rows.push(cue.text.slice(0,10000));if(rows.length>=200)return rows;}
 }
 return rows;
}
/** Company display action restricted to placed, unlocked targets, including shared-asset protection. */
export function setEditableCaptionDisplayLanguage(state:EditorState,language:'source'|'translated'){
 const targets=captionTranslationTargets(state.undoableState),document=state.undoableState;
 const assets=Object.fromEntries(Object.entries(document.assets).filter(([id])=>targets.captionAssets.has(id)&&!targets.protectedAssets.has(id)));
 const items=Object.fromEntries(Object.entries(document.items).filter(([id,item])=>targets.editableItems.has(id)&&(item.type!=='captions'||!!assets[item.assetId])));
 const subset={...state,undoableState:{...document,assets,items}};
 const next=setCaptionDisplayLanguage(subset,language);
 return next===subset?state:{...state,undoableState:{...document,assets:{...document.assets,...next.undoableState.assets},items:{...document.items,...next.undoableState.items}}};
}
